import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { storePurchaseOrdersAPI, localVendorsAPI, productsAPI, vendorsAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, RefreshCw, ShoppingCart, Plus, Trash2, Send, 
  Eye, XCircle, Package, Building2, Truck, CheckCircle, Clock
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const initialPOForm = {
  po_type: 'vendor_purchase',  // Unified vendor purchase (local + allowed central)
  vendor_id: '',
  vendor_source: '',  // 'local' or 'central' - auto-set based on selected vendor
  expected_delivery_date: '',
  notes: '',
  items: []
};

const StorePurchaseOrders = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('list');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  
  // Create PO
  const [vendors, setVendors] = useState([]);  // Local vendors
  const [centralVendors, setCentralVendors] = useState([]);  // Allowed central vendors
  const [allVendors, setAllVendors] = useState([]);  // Combined list for unified dropdown
  const [products, setProducts] = useState([]);
  const [poForm, setPoForm] = useState(initialPOForm);
  const [productSearch, setProductSearch] = useState('');
  const [savingPO, setSavingPO] = useState(false);
  
  // View PO
  const [selectedPO, setSelectedPO] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Receive Dialog
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveItems, setReceiveItems] = useState([]);
  
  const storeType = user?.store_type || 'owned';
  const isFranchise = storeType === 'franchise';

  const fetchPurchaseOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter && typeFilter !== 'all') params.po_type = typeFilter;
      if (search) params.search = search;
      
      const response = await storePurchaseOrdersAPI.list(params);
      setPurchaseOrders(response.data.items || []);
    } catch (error) {
      console.error('Error fetching POs:', error);
      toast({ title: 'Error', description: 'Failed to fetch purchase orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, search, toast]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await storePurchaseOrdersAPI.getStats();
      setStats(response.data || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const response = await localVendorsAPI.list({ page_size: 100, is_active: true });
      const localVendorsList = (response.data.items || []).map(v => ({
        ...v,
        vendor_source: 'local',
        display_type: 'Local Vendor'
      }));
      setVendors(localVendorsList);
      return localVendorsList;
    } catch (error) {
      console.error('Error fetching local vendors:', error);
      return [];
    }
  }, []);

  // Fetch central vendors for franchise direct purchase
  const fetchCentralVendors = useCallback(async () => {
    if (!isFranchise) return [];
    try {
      const response = await vendorsAPI.getFranchiseDirectVendors();
      const centralVendorsList = (response.data || []).map(v => ({
        ...v,
        vendor_source: 'central',
        display_type: 'Central Vendor (HQ Approved)'
      }));
      setCentralVendors(centralVendorsList);
      return centralVendorsList;
    } catch (error) {
      console.error('Error fetching central vendors:', error);
      return [];
    }
  }, [isFranchise]);

  // Combine local and central vendors into unified list
  const fetchAllVendors = useCallback(async () => {
    const [localList, centralList] = await Promise.all([
      fetchVendors(),
      fetchCentralVendors()
    ]);
    
    // Combine: Central vendors first (if franchise), then local vendors
    const combined = isFranchise 
      ? [...centralList, ...localList]
      : localList;
    
    setAllVendors(combined);
  }, [fetchVendors, fetchCentralVendors, isFranchise]);

  const fetchProducts = useCallback(async (searchTerm = '') => {
    // For vendor_purchase PO type: Only fetch products when vendor is selected AND search has 2+ chars
    // For hq_stock PO type: Fetch all products with search filter
    if (poForm.po_type === 'vendor_purchase') {
      if (!poForm.vendor_id) {
        setProducts([]);
        return;
      }
      if (searchTerm.length < 2) {
        setProducts([]);
        return;
      }
      
      // Fetch products from the selected vendor
      try {
        const isLocalVendor = poForm.vendor_source === 'local';
        const params = { search: searchTerm, page_size: 50 };
        
        let vendorProducts = [];
        if (isLocalVendor) {
          const response = await localVendorsAPI.getProducts(poForm.vendor_id, params);
          vendorProducts = response.data.items || [];
        } else {
          const response = await vendorsAPI.getProducts(poForm.vendor_id, params);
          vendorProducts = response.data.items || [];
        }
        
        // If vendor has products mapped, use them
        if (vendorProducts.length > 0) {
          setProducts(vendorProducts);
        } else {
          // Fallback to all products search if vendor has no products mapped
          const fallbackResponse = await productsAPI.list({ 
            page_size: 50, 
            is_active: true, 
            search: searchTerm 
          });
          setProducts(fallbackResponse.data.items || fallbackResponse.data || []);
        }
      } catch (error) {
        console.error('Error fetching vendor products:', error);
        // If API error, fallback to all products search
        try {
          const params = { page_size: 50, is_active: true, search: searchTerm };
          const response = await productsAPI.list(params);
          setProducts(response.data.items || response.data || []);
        } catch (fallbackError) {
          console.error('Error fetching fallback products:', fallbackError);
          setProducts([]);
        }
      }
    } else if (poForm.po_type === 'hq_stock') {
      // For HQ Stock POs, search all products (no vendor limit)
      if (searchTerm.length < 2) {
        setProducts([]);
        return;
      }
      try {
        const params = { page_size: 50, is_active: true, search: searchTerm };
        const response = await productsAPI.list(params);
        setProducts(response.data.items || response.data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      }
    }
  }, [poForm.po_type, poForm.vendor_id, poForm.vendor_source]);

  // Debounce timer ref for product search
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    fetchPurchaseOrders();
    fetchStats();
    fetchAllVendors();
  }, [fetchPurchaseOrders, fetchStats, fetchAllVendors]);

  // Fetch products with debounced search - only when conditions are met
  useEffect(() => {
    if (activeTab === 'create') {
      // Clear any pending debounce
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      
      // Debounce the search to avoid excessive API calls
      searchDebounceRef.current = setTimeout(() => {
        fetchProducts(productSearch);
      }, 300);
      
      return () => {
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }
      };
    }
  }, [activeTab, productSearch, fetchProducts]);
  
  // Clear products when vendor changes
  useEffect(() => {
    setProducts([]);
    setProductSearch('');
  }, [poForm.vendor_id]);

  const addItemToPO = async (product) => {
    // Check if already added
    if (poForm.items.find(i => i.product_id === product.id)) {
      toast({ title: 'Already Added', description: 'Product is already in the order', variant: 'destructive' });
      return;
    }
    
    // Fetch last purchase rate for this product
    let lastPurchaseRate = product.cost_price || 0;
    try {
      const rateResponse = await productsAPI.getLastPurchaseRate(product.id);
      if (rateResponse.data.last_rate) {
        lastPurchaseRate = rateResponse.data.last_rate;
      }
    } catch (error) {
      console.log('Could not fetch last purchase rate, using cost price');
    }
    
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      hsn_code: product.hsn_code,
      uom: product.base_uom || 'PCS',
      ordered_qty: 1,
      unit_price: lastPurchaseRate,
      tax_rate: product.gst_rate || 0,
      discount_percent: 0,
      cost_price: product.cost_price || 0,  // Keep original cost price for reference
      last_purchase_rate: lastPurchaseRate  // Store for display
    };
    
    setPoForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateItemQty = (productId, qty) => {
    setPoForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.product_id === productId ? { ...item, ordered_qty: parseFloat(qty) || 0 } : item
      )
    }));
  };

  const updateItemPrice = (productId, price) => {
    setPoForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.product_id === productId ? { ...item, unit_price: parseFloat(price) || 0 } : item
      )
    }));
  };

  const removeItem = (productId) => {
    setPoForm(prev => ({
      ...prev,
      items: prev.items.filter(item => item.product_id !== productId)
    }));
  };

  const calculateTotal = () => {
    return poForm.items.reduce((sum, item) => {
      const subtotal = item.ordered_qty * item.unit_price;
      const tax = subtotal * (item.tax_rate / 100);
      return sum + subtotal + tax;
    }, 0);
  };

  const handleCreatePO = async () => {
    // Validate vendor selection for vendor purchase type only
    if (poForm.po_type === 'vendor_purchase' && !poForm.vendor_id) {
      toast({ title: 'Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }
    if (poForm.items.length === 0) {
      toast({ title: 'Error', description: 'Please add at least one item', variant: 'destructive' });
      return;
    }

    setSavingPO(true);
    try {
      // Get the selected vendor to determine if it's local or central
      const selectedVendor = allVendors.find(v => v.id === poForm.vendor_id);
      const vendorSource = selectedVendor?.vendor_source || 'local';
      
      // Determine the correct PO type for backend
      let backendPOType = 'local_vendor';
      if (poForm.po_type === 'hq_stock') {
        backendPOType = 'hq_stock';  // Request from HQ - no vendor needed
      }
      
      const data = {
        po_type: backendPOType,
        vendor_id: poForm.po_type === 'vendor_purchase' ? poForm.vendor_id : null,
        vendor_source: poForm.po_type === 'vendor_purchase' ? vendorSource : null,
        expected_delivery_date: poForm.expected_delivery_date || null,
        notes: poForm.notes,
        items: poForm.items.map(item => ({
          product_id: item.product_id,
          ordered_qty: item.ordered_qty,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent || 0,
          tax_rate: item.tax_rate || 0
        }))
      };

      const response = await storePurchaseOrdersAPI.create(data);
      
      // For HQ stock requests, show appropriate message
      if (poForm.po_type === 'hq_stock') {
        toast({ 
          title: 'Success', 
          description: `Request ${response.data.po_number} created. Submit to HQ for approval.` 
        });
      } else {
        toast({ title: 'Success', description: `Purchase Order ${response.data.po_number} created` });
      }
      
      setPoForm(initialPOForm);
      setActiveTab('list');
      fetchPurchaseOrders();
      fetchStats();
    } catch (error) {
      console.error('Error creating PO:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to create purchase order', 
        variant: 'destructive' 
      });
    } finally {
      setSavingPO(false);
    }
  };

  const handleSubmitPO = async (poId) => {
    try {
      await storePurchaseOrdersAPI.submit(poId);
      toast({ title: 'Success', description: 'Purchase Order submitted for approval' });
      fetchPurchaseOrders();
      fetchStats();
    } catch (error) {
      console.error('Error submitting PO:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to submit purchase order', 
        variant: 'destructive' 
      });
    }
  };

  const handleCancelPO = async (poId) => {
    if (!window.confirm('Are you sure you want to cancel this PO?')) return;
    
    try {
      await storePurchaseOrdersAPI.cancel(poId, 'Cancelled by user');
      toast({ title: 'Success', description: 'Purchase Order cancelled' });
      fetchPurchaseOrders();
      fetchStats();
    } catch (error) {
      console.error('Error cancelling PO:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to cancel purchase order', 
        variant: 'destructive' 
      });
    }
  };

  const openReceiveDialog = (po) => {
    setSelectedPO(po);
    setReceiveItems(po.items.map(item => ({
      product_id: item.product_id,
      product_name: item.product_name,
      ordered_qty: item.ordered_qty,
      approved_qty: item.approved_qty,
      already_received: item.received_qty || 0,
      received_qty: 0
    })));
    setReceiveDialogOpen(true);
  };

  const handleReceivePO = async () => {
    try {
      const data = {
        items: receiveItems.filter(item => item.received_qty > 0).map(item => ({
          product_id: item.product_id,
          received_qty: item.received_qty
        })),
        notes: ''
      };
      
      await storePurchaseOrdersAPI.receive(selectedPO.id, data);
      toast({ title: 'Success', description: 'Items received successfully' });
      setReceiveDialogOpen(false);
      fetchPurchaseOrders();
      fetchStats();
    } catch (error) {
      console.error('Error receiving PO:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to receive items', 
        variant: 'destructive' 
      });
    }
  };

  const viewPO = (po) => {
    setSelectedPO(po);
    setViewDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Draft' },
      pending_approval: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending Approval' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Processing' },
      dispatched: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Dispatched' },
      partially_received: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Partial' },
      completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completed' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
      cancelled: { bg: 'bg-gray-200', text: 'text-gray-600', label: 'Cancelled' }
    };
    const c = config[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
    return <Badge className={`${c.bg} ${c.text}`}>{c.label}</Badge>;
  };

  const getTypeBadge = (poType, vendorSource) => {
    if (poType === 'local_vendor') {
      // Check vendor_source to differentiate
      if (vendorSource === 'central') {
        return <Badge className="bg-purple-100 text-purple-700">Central Vendor</Badge>;
      }
      return <Badge className="bg-teal-100 text-teal-700">Local Vendor</Badge>;
    }
    if (poType === 'central_vendor_direct') {
      return <Badge className="bg-purple-100 text-purple-700">Central Vendor</Badge>;
    }
    return <Badge className="bg-indigo-100 text-indigo-700">HQ Stock</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="store-purchase-orders-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-blue-600" />
            Purchase Orders
          </h1>
          <p className="text-gray-500 mt-1">
            {isFranchise 
              ? 'Create purchase orders from vendors (local + HQ approved central) or request stock from HQ'
              : 'Create purchase orders from approved local vendors'
            }
          </p>
        </div>
        <Button onClick={fetchPurchaseOrders} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Total POs</p>
            <p className="text-2xl font-bold text-blue-600">
              {Object.values(stats.by_status || {}).reduce((sum, s) => sum + (s.count || 0), 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Pending Approval</p>
            <p className="text-2xl font-bold text-yellow-600">
              {stats.by_status?.pending_approval?.count || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Approved</p>
            <p className="text-2xl font-bold text-green-600">
              {stats.by_status?.approved?.count || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-emerald-600">
              {stats.by_status?.completed?.count || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="list">
            <Package className="w-4 h-4 mr-2" />
            PO List
          </TabsTrigger>
          <TabsTrigger value="create">
            <Plus className="w-4 h-4 mr-2" />
            Create PO
          </TabsTrigger>
        </TabsList>

        {/* PO List Tab */}
        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search PO number..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div className="w-[150px]">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="pending_approval">Pending Approval</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[180px]">
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="local_vendor">Vendor Purchase</SelectItem>
                      {isFranchise && <SelectItem value="hq_stock">HQ Stock</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* PO Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>PO Number</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Vendor/Source</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : purchaseOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No purchase orders found
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchaseOrders.map((po) => (
                      <TableRow key={po.id} data-testid={`po-row-${po.id}`}>
                        <TableCell className="font-mono text-sm">{po.po_number}</TableCell>
                        <TableCell>{getTypeBadge(po.po_type, po.vendor_source)}</TableCell>
                        <TableCell>
                          {po.po_type === 'local_vendor' ? (
                            <div className="flex items-center gap-2">
                              <Truck className="w-4 h-4 text-gray-400" />
                              <span>{po.vendor_name || '-'}</span>
                              {po.vendor_source === 'central' && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200">HQ</Badge>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              <span>{po.source_warehouse_name || 'HQ'}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(po.created_at)}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(po.grand_total)}
                        </TableCell>
                        <TableCell className="text-center">{getStatusBadge(po.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => viewPO(po)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            {po.status === 'draft' && po.po_type === 'hq_stock' && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-blue-600"
                                onClick={() => handleSubmitPO(po.id)}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                            {po.po_type === 'local_vendor' && ['approved', 'partially_received'].includes(po.status) && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-green-600"
                                onClick={() => openReceiveDialog(po)}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {['draft', 'pending_approval'].includes(po.status) && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-600"
                                onClick={() => handleCancelPO(po.id)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Create PO Tab */}
        <TabsContent value="create" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* PO Details */}
            <Card className="lg:col-span-2">
              <CardHeader className="py-3 bg-gray-50/50 border-b">
                <CardTitle className="text-lg">Order Details</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Order Type *</Label>
                    <Select 
                      value={poForm.po_type} 
                      onValueChange={(val) => setPoForm(prev => ({ ...prev, po_type: val, vendor_id: '', vendor_source: '' }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vendor_purchase">Vendor Purchase</SelectItem>
                        {isFranchise && <SelectItem value="hq_stock">Request from HQ</SelectItem>}
                      </SelectContent>
                    </Select>
                    {isFranchise && poForm.po_type === 'vendor_purchase' && (
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Includes local vendors + HQ approved central vendors
                      </p>
                    )}
                  </div>
                  
                  {poForm.po_type === 'vendor_purchase' && (
                    <div className="space-y-2">
                      <Label>Select Vendor *</Label>
                      <Select 
                        value={poForm.vendor_id} 
                        onValueChange={(val) => {
                          const vendor = allVendors.find(v => v.id === val);
                          setPoForm(prev => ({ 
                            ...prev, 
                            vendor_id: val,
                            vendor_source: vendor?.vendor_source || 'local'
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose vendor..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {/* Central Vendors Section (if franchise and available) */}
                          {isFranchise && centralVendors.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 sticky top-0">
                                HQ Approved Central Vendors
                              </div>
                              {centralVendors.map(vendor => (
                                <SelectItem key={vendor.id} value={vendor.id}>
                                  <div className="flex items-center gap-2">
                                    <span>{vendor.name}</span>
                                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200 ml-1">
                                      Central
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </>
                          )}
                          
                          {/* Local Vendors Section */}
                          {vendors.length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 sticky top-0">
                                Local Vendors
                              </div>
                              {vendors.map(vendor => (
                                <SelectItem key={vendor.id} value={vendor.id}>
                                  <div className="flex items-center gap-2">
                                    <span>{vendor.name}</span>
                                    <Badge variant="outline" className="text-xs bg-teal-50 text-teal-600 border-teal-200 ml-1">
                                      Local
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </>
                          )}
                          
                          {allVendors.length === 0 && (
                            <div className="px-2 py-4 text-sm text-gray-500 text-center">
                              No vendors available
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Expected Delivery Date</Label>
                    <Input
                      type="date"
                      value={poForm.expected_delivery_date}
                      onChange={(e) => setPoForm(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={poForm.notes}
                    onChange={(e) => setPoForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Order notes..."
                    rows={2}
                  />
                </div>

                {/* Items Table */}
                <div className="space-y-2">
                  <Label>Order Items ({poForm.items.length})</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Product</TableHead>
                          <TableHead className="w-[100px]">Qty</TableHead>
                          <TableHead className="w-[120px]">Unit Price</TableHead>
                          <TableHead className="w-[80px]">Tax %</TableHead>
                          <TableHead className="text-right w-[100px]">Total</TableHead>
                          <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {poForm.items.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-4 text-gray-500">
                              No items added. Search and add products from the right panel.
                            </TableCell>
                          </TableRow>
                        ) : (
                          poForm.items.map((item) => {
                            const subtotal = item.ordered_qty * item.unit_price;
                            const tax = subtotal * (item.tax_rate / 100);
                            const total = subtotal + tax;
                            return (
                              <TableRow key={item.product_id}>
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-sm">{item.product_name}</p>
                                    <p className="text-xs text-gray-500">{item.product_sku}</p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={item.ordered_qty}
                                    onChange={(e) => updateItemQty(item.product_id, e.target.value)}
                                    className="h-8"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={item.unit_price}
                                    onChange={(e) => updateItemPrice(item.product_id, e.target.value)}
                                    className="h-8"
                                  />
                                </TableCell>
                                <TableCell className="text-sm text-gray-600">{item.tax_rate}%</TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(total)}</TableCell>
                                <TableCell>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="text-red-600"
                                    onClick={() => removeItem(item.product_id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2 text-sm">
                    <div className="flex justify-between font-bold text-lg pt-2 border-t">
                      <span>Grand Total</span>
                      <span className="text-blue-600">{formatCurrency(calculateTotal())}</span>
                    </div>
                  </div>
                </div>

                {/* Create Button */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => setPoForm(initialPOForm)}
                  >
                    Clear
                  </Button>
                  <Button 
                    onClick={handleCreatePO}
                    disabled={savingPO}
                    className={poForm.po_type === 'hq_stock' ? "bg-purple-600 hover:bg-purple-700" : "bg-blue-600 hover:bg-blue-700"}
                  >
                    {savingPO ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : poForm.po_type === 'hq_stock' ? (
                      <>
                        <Building2 className="w-4 h-4 mr-2" />
                        Create HQ Request
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Create Purchase Order
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Product Search */}
            <Card>
              <CardHeader className="py-3 bg-gray-50/50 border-b">
                <CardTitle className="text-lg">Add Products</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder={poForm.po_type === 'hq_stock' ? "Type 2+ characters to search all products..." : "Type 2+ characters to search vendor products..."}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pl-9"
                    disabled={poForm.po_type === 'vendor_purchase' && !poForm.vendor_id}
                  />
                </div>
                
                {/* Helper message based on state */}
                {poForm.po_type === 'vendor_purchase' && !poForm.vendor_id && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    Please select a vendor first
                  </div>
                )}
                
                {poForm.po_type === 'vendor_purchase' && poForm.vendor_id && productSearch.length < 2 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    Type at least 2 characters to search products
                  </div>
                )}
                
                {poForm.po_type === 'hq_stock' && productSearch.length < 2 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    Type at least 2 characters to search all products
                  </div>
                )}
                
                {productSearch.length >= 2 && products.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No products found matching "{productSearch}"
                  </div>
                )}
                
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {products.slice(0, 20).map(product => (
                    <div 
                      key={product.id}
                      className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => addItemToPO(product)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{product.name}</p>
                          <p className="text-xs text-gray-500">{product.sku}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-blue-600">
                            {formatCurrency(product.cost_price)}
                          </p>
                          <p className="text-xs text-gray-500">Cost Price | GST: {product.gst_rate}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* View PO Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              {selectedPO?.po_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPO && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Type</p>
                  <p className="font-medium">{getTypeBadge(selectedPO.po_type)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <p className="font-medium">{getStatusBadge(selectedPO.status)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">{formatDate(selectedPO.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total</p>
                  <p className="font-medium text-blue-600">{formatCurrency(selectedPO.grand_total)}</p>
                </div>
              </div>
              
              {selectedPO.po_type === 'local_vendor' && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500">Vendor</p>
                  <p className="font-medium">{selectedPO.vendor_name}</p>
                  <p className="text-sm text-gray-500">{selectedPO.vendor_code}</p>
                </div>
              )}
              
              {selectedPO.source_warehouse_name && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-600">Source Warehouse</p>
                  <p className="font-medium">{selectedPO.source_warehouse_name}</p>
                </div>
              )}
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                    <TableHead className="text-right">Approved</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedPO.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <p className="font-medium text-sm">{item.product_name}</p>
                        <p className="text-xs text-gray-500">{item.product_sku}</p>
                      </TableCell>
                      <TableCell className="text-right">{item.ordered_qty}</TableCell>
                      <TableCell className="text-right">{item.approved_qty || item.ordered_qty}</TableCell>
                      <TableCell className="text-right">{item.received_qty || 0}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.line_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {selectedPO.notes && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm text-yellow-700">Notes: {selectedPO.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Receive Items - {selectedPO?.po_number}
            </DialogTitle>
            <DialogDescription>
              Enter quantities received from vendor
            </DialogDescription>
          </DialogHeader>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Ordered</TableHead>
                <TableHead className="text-right">Already Received</TableHead>
                <TableHead className="w-[120px]">Receive Now</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {receiveItems.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium text-sm">{item.product_name}</TableCell>
                  <TableCell className="text-right">{item.approved_qty || item.ordered_qty}</TableCell>
                  <TableCell className="text-right">{item.already_received}</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      max={(item.approved_qty || item.ordered_qty) - item.already_received}
                      value={item.received_qty}
                      onChange={(e) => {
                        const newItems = [...receiveItems];
                        newItems[idx].received_qty = parseFloat(e.target.value) || 0;
                        setReceiveItems(newItems);
                      }}
                      className="h-8"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReceivePO}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirm Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorePurchaseOrders;
